import { MapContainer, TileLayer, Marker, Popup, ZoomControl } from 'react-leaflet';
import { LatLngExpression } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useState } from 'react';

// Modern dot marker style
const customIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="marker-dot"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10]
});

const styleSheet = `
  .custom-div-icon {
    background: transparent;
    border: none;
  }
  
  .marker-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #3b82f6;
    border: 2px solid white;
    box-shadow: 0 0 4px rgba(0,0,0,0.2);
    transition: transform 0.2s ease;
  }
  
  .leaflet-marker-icon:hover .marker-dot {
    transform: scale(1.2);
    background: #2563eb;
  }
  
  .leaflet-popup-content-wrapper {
    border-radius: 8px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    max-width: 400px;
  }
  
  .popup-content {
    padding: 0.75rem;
  }
  
  .popup-content h3 {
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
    color: #334155;
  }
  
  .popup-content p {
    color: #64748b;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .media-carousel {
    position: relative;
    width: 100%;
    height: 200px;
    overflow: hidden;
    border-radius: 8px;
    margin-top: 0.5rem;
  }

  .media-item {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .carousel-controls {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    background: rgba(0, 0, 0, 0.5);
    padding: 4px 8px;
    border-radius: 16px;
  }

  .carousel-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .carousel-dot.active {
    background: white;
  }

  .carousel-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    padding: 4px 8px;
    font-size: 0.75rem;
  }

  /* Custom zoom control styles */
  .leaflet-control-zoom {
    border: none !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
  }

  .leaflet-control-zoom-in,
  .leaflet-control-zoom-out {
    width: 36px !important;
    height: 36px !important;
    line-height: 36px !important;
    background-color: white !important;
    border: 1px solid #e2e8f0 !important;
    color: #334155 !important;
    font-size: 18px !important;
    transition: all 0.2s ease !important;
  }

  .leaflet-control-zoom-in {
    border-radius: 8px 8px 0 0 !important;
  }

  .leaflet-control-zoom-out {
    border-radius: 0 0 8px 8px !important;
  }

  .leaflet-control-zoom-in:hover,
  .leaflet-control-zoom-out:hover {
    background-color: #f8fafc !important;
    color: #2563eb !important;
  }
`;

interface Location {
  id: string;
  name: string;
  coordinates: LatLngExpression;
  description?: string;
  date?: string;
  media?: {
    type: 'image' | 'video';
    url: string;
    caption?: string;
  }[];
}

export const travelLocations: Location[] = [
  { id: '1', name: 'Madrid, Spain', coordinates: [40.4167754, -3.7037902] },
  { id: '2', name: 'Düsseldorf, Germany', coordinates: [51.2277411, 6.7734556] },
  { id: '3', name: 'Paris, France', coordinates: [48.8566, 2.3522] },
  { id: '4', name: 'Valencia, Spain', coordinates: [39.4699, -0.3763] },
  { id: '5', name: 'Malaga, Spain', coordinates: [36.7213, -4.4217] },
  { id: '6', name: 'Palma de Mallorca, Spain', coordinates: [39.5696, 2.6502] },
  { id: '7', name: 'Sevilla, Spain', coordinates: [37.3891, -5.9845] },
  { id: '8', name: 'Marbella, Spain', coordinates: [36.5097, -4.8861] },
  { id: '9', name: 'Alicante, Spain', coordinates: [38.3452, -0.4815] },
  { id: '10', name: 'Barcelona, Spain', coordinates: [41.3851, 2.1734] },
  { id: '11', name: 'Santander, Spain', coordinates: [43.4623, -3.8099] },
  { id: '12', name: 'Bilbao, Spain', coordinates: [43.2630, -2.9350] },
  { id: '13', name: 'San Sebastian, Spain', coordinates: [43.3183, -1.9812] },
  { id: '14', name: 'Andorra, Andorra', coordinates: [42.5063, 1.5218] },
  { id: '15', name: 'Lisboa, Portugal', coordinates: [38.7223, -9.1393] },
  { id: '16', name: 'Porto, Portugal', coordinates: [41.1579, -8.6291] },
  { id: '17', name: 'Toulouse, France', coordinates: [43.6047, 1.4442] },
  { id: '18', name: 'Perpignan, France', coordinates: [42.6986, 2.8956] },
  { id: '19', name: 'Nantes, France', coordinates: [47.2184, -1.5536] },
  { id: '226', name: 'Colmar, France', coordinates: [48.0794, 7.3585] },
  { id: '227', name: 'Strasbourg, France', coordinates: [48.5734, 7.7521] },
  { id: '195', name: 'Monaco', coordinates: [43.7384, 7.4246] },
  { id: '20', name: 'Basel, Switzerland', coordinates: [47.5596, 7.5886] },
  { id: '21', name: 'Bern, Switzerland', coordinates: [46.9480, 7.4474] },
  { id: '22', name: 'Lille, France', coordinates: [50.6292, 3.0573] },
  { id: '23', name: 'Luxembourg, Luxembourg', coordinates: [49.8153, 6.1296] },
  { id: '24', name: 'Aachen, Germany', coordinates: [50.7753, 6.0839] },
  { id: '25', name: 'Brussels, Belgium', coordinates: [50.8503, 4.3517] },
  { id: '26', name: 'Brugge, Belgium', coordinates: [51.2093, 3.2247] },
  { id: '27', name: 'Utrecht, Netherlands', coordinates: [52.0907, 5.1214] },
  { id: '28', name: 'Amsterdam, Netherlands', coordinates: [52.3676, 4.9041] },
  { id: '29', name: 'Köln, Germany', coordinates: [50.9375, 6.9603] },
  { id: '30', name: 'Koblenz, Germany', coordinates: [50.3569, 7.5890] },
  { id: '31', name: 'Bonn, Germany', coordinates: [50.7374, 7.0982] },
  { id: '32', name: 'Frankfurt, Germany', coordinates: [50.1109, 8.6821] },
  { id: '33', name: 'Venlo, Netherlands', coordinates: [51.3703, 6.1724] },
  { id: '34', name: 'Rotterdam, Netherlands', coordinates: [51.9244, 4.4777] },
  { id: '35', name: 'Den Haag, Netherlands', coordinates: [52.0705, 4.3007] },
  { id: '36', name: 'Eindhoven, Netherlands', coordinates: [51.4416, 5.4697] },
  { id: '37', name: 'Maastricht, Netherlands', coordinates: [50.8514, 5.6910] },
  { id: '38', name: 'Stuttgart, Germany', coordinates: [48.7758, 9.1829] },
  { id: '39', name: 'Berlin, Germany', coordinates: [52.5200, 13.4050] },
  { id: '40', name: 'Heidelberg, Germany', coordinates: [49.3988, 8.6724] },
  { id: '41', name: 'Prague, Czechia', coordinates: [50.0755, 14.4378] },
  { id: '42', name: 'Regensburg, Germany', coordinates: [49.0134, 12.1016] },
  { id: '43', name: 'Nuremberg, Germany', coordinates: [49.4521, 11.0767] },
  { id: '44', name: 'Dresden, Germany', coordinates: [51.0504, 13.7373] },
  { id: '45', name: 'Hamburg, Germany', coordinates: [53.5511, 9.9937] },
  { id: '46', name: 'Bremen, Germany', coordinates: [53.0793, 8.8017] },
  { id: '47', name: 'Munich, Germany', coordinates: [48.1351, 11.5820] },
  { id: '48', name: 'Zurich, Switzerland', coordinates: [47.3769, 8.5417] },
  { id: '49', name: 'Lucern, Switzerland', coordinates: [47.0502, 8.3093] },
  { id: '50', name: 'Milan, Italy', coordinates: [45.4642, 9.1900] },
  { id: '51', name: 'Genoa, Italy', coordinates: [44.4056, 8.9463] },
  { id: '52', name: 'Venice, Italy', coordinates: [45.4408, 12.3155] },
  { id: '53', name: 'Parma, Italy', coordinates: [44.8015, 10.3279] },
  { id: '54', name: 'Bologna, Italy', coordinates: [44.4949, 11.3426] },
  { id: '55', name: 'Florence, Italy', coordinates: [43.7696, 11.2558] },
  { id: '56', name: 'Rome, Italy', coordinates: [41.9028, 12.4964] },
  { id: '57', name: 'San Marino', coordinates: [43.9424, 12.4578] },
  { id: '58', name: 'Pisa, Italy', coordinates: [43.7228, 10.4017] },
  { id: '59', name: 'Bari, Italy', coordinates: [41.1171, 16.8719] },
  { id: '60', name: 'Tokyo, Japan', coordinates: [35.6762, 139.6503] },
  { id: '61', name: 'Kyoto, Japan', coordinates: [35.0116, 135.7681] },
  { id: '62', name: 'Naples, Italy', coordinates: [40.8518, 14.2681] },
  
  // New locations - Balkans and Eastern Europe
  { id: '63', name: 'Tirana, Albania', coordinates: [41.3275, 19.8187] },
  { id: '64', name: 'Skopje, North Macedonia', coordinates: [42.0024, 21.4361] },
  { id: '65', name: 'Pristina, Kosovo', coordinates: [42.6629, 21.1655] },
  { id: '66', name: 'Kotor, Montenegro', coordinates: [42.4207, 18.7719] },
  { id: '67', name: 'Split, Croatia', coordinates: [43.5081, 16.4402] },
  { id: '68', name: 'Sarajevo, Bosnia and Herzegovina', coordinates: [43.8563, 18.4131] },
  { id: '69', name: 'Zagreb, Croatia', coordinates: [45.8150, 15.9819] },
  { id: '70', name: 'Trieste, Italy', coordinates: [45.6495, 13.7768] },
  { id: '71', name: 'Ljubljana, Slovenia', coordinates: [46.0569, 14.5058] },
  { id: '72', name: 'Budapest, Hungary', coordinates: [47.4979, 19.0402] },
  { id: '73', name: 'Belgrade, Serbia', coordinates: [44.7866, 20.4489] },
  { id: '74', name: 'Bucharest, Romania', coordinates: [44.4268, 26.1025] },
  
  // Austria and Slovakia
  { id: '75', name: 'Vienna, Austria', coordinates: [48.2082, 16.3738] },
  { id: '76', name: 'Salzburg, Austria', coordinates: [47.8095, 13.0550] },
  { id: '77', name: 'Innsbruck, Austria', coordinates: [47.2692, 11.4041] },
  { id: '78', name: 'Bratislava, Slovakia', coordinates: [48.1486, 17.1077] },
  { id: '79', name: 'Bregenz, Austria', coordinates: [47.5026, 9.7472] },
  
  // Switzerland
  { id: '80', name: 'Interlaken, Switzerland', coordinates: [46.6863, 7.8632] },
  { id: '81', name: 'Lauterbrunnen, Switzerland', coordinates: [46.5937, 7.9090] },
  { id: '82', name: 'Grindelwald, Switzerland', coordinates: [46.6244, 8.0344] },
  
  // United Kingdom and Scotland
  { id: '83', name: 'London, UK', coordinates: [51.5074, -0.1278] },
  { id: '84', name: 'Oxford, UK', coordinates: [51.7520, -1.2577] },
  { id: '85', name: 'Birmingham, UK', coordinates: [52.4862, -1.8904] },
  { id: '86', name: 'Liverpool, UK', coordinates: [53.4084, -2.9916] },
  { id: '87', name: 'Edinburgh, Scotland', coordinates: [55.9533, -3.1883] },
  { id: '88', name: 'Glasgow, Scotland', coordinates: [55.8642, -4.2518] },
  { id: '89', name: 'St Andrews, Scotland', coordinates: [56.3398, -2.7967] },
  
  // Ireland and Northern Ireland
  { id: '90', name: 'Dublin, Ireland', coordinates: [53.3498, -6.2603] },
  { id: '91', name: 'Belfast, Northern Ireland', coordinates: [54.5973, -5.9301] },
  { id: '92', name: 'Londonderry, Northern Ireland', coordinates: [54.9966, -7.3086] },

  // Additional Spanish Cities
  { id: '93', name: 'Burgos, Spain', coordinates: [42.3439, -3.6969] },
  { id: '94', name: 'Pamplona, Spain', coordinates: [42.8125, -1.6458] },
  { id: '95', name: 'Valladolid, Spain', coordinates: [41.6523, -4.7245] },
  { id: '96', name: 'Toledo, Spain', coordinates: [39.8628, -4.0273] },

  // Poland
  { id: '97', name: 'Warsaw, Poland', coordinates: [52.2297, 21.0122] },
  { id: '98', name: 'Torun, Poland', coordinates: [53.0138, 18.5984] },
  { id: '99', name: 'Krakow, Poland', coordinates: [50.0647, 19.9450] },
  { id: '100', name: 'Gdansk, Poland', coordinates: [54.3520, 18.6466] },

  // Nordic Countries
  { id: '101', name: 'Copenhagen, Denmark', coordinates: [55.6761, 12.5683] },
  { id: '102', name: 'Lund, Sweden', coordinates: [55.7047, 13.1910] },
  { id: '103', name: 'Stockholm, Sweden', coordinates: [59.3293, 18.0686] },
  { id: '104', name: 'Oslo, Norway', coordinates: [59.9139, 10.7522] },
  { id: '105', name: 'Malmo, Sweden', coordinates: [55.6034, 13.0007] },
  { id: '106', name: 'Kristiansand, Norway', coordinates: [58.1599, 8.0182] },
  { id: '107', name: 'Stavanger, Norway', coordinates: [58.9700, 5.7331] },
  { id: '108', name: 'Bergen, Norway', coordinates: [60.3913, 5.3221] },

  // Baltic States and Russia
  { id: '109', name: 'Tallinn, Estonia', coordinates: [59.4370, 24.7536] },
  { id: '110', name: 'Riga, Latvia', coordinates: [56.9496, 24.1052] },
  { id: '111', name: 'Vilnius, Lithuania', coordinates: [54.6872, 25.2797] },
  { id: '112', name: 'St Petersburg, Russia', coordinates: [59.9311, 30.3609] },
  { id: '113', name: 'Helsinki, Finland', coordinates: [60.1699, 24.9384] },
  { id: '228', name: 'Reykjavik, Iceland', coordinates: [64.1466, -21.9426] },

  // Middle East and Caucasus
  { id: '114', name: 'Dubai, UAE', coordinates: [25.2048, 55.2708] },
  { id: '115', name: 'Yerevan, Armenia', coordinates: [40.1772, 44.5034] },
  { id: '116', name: 'Baku, Azerbaijan', coordinates: [40.4093, 49.8671] },
  { id: '117', name: 'Tbilisi, Georgia', coordinates: [41.7151, 44.8271] },

  // Mediterranean Islands and Greece
  { id: '118', name: 'Paphos, Cyprus', coordinates: [34.7720, 32.4297] },
  { id: '119', name: 'Valletta, Malta', coordinates: [35.8989, 14.5146] },
  { id: '120', name: 'Santorini, Greece', coordinates: [36.3932, 25.4615] },
  { id: '121', name: 'Corfu, Greece', coordinates: [39.6243, 19.9217] },
  { id: '122', name: 'Athens, Greece', coordinates: [37.9838, 23.7275] },
  { id: '123', name: 'Thessaloniki, Greece', coordinates: [40.6401, 22.9444] },

  // Additional German Cities
  { id: '124', name: 'Kempen, Germany', coordinates: [51.3647, 6.4172] },
  { id: '125', name: 'Cochem, Germany', coordinates: [50.1474, 7.1675] },
  { id: '126', name: 'Idstein, Germany', coordinates: [50.2219, 8.2675] },
  { id: '127', name: 'Limburg an der Lahn, Germany', coordinates: [50.3833, 8.0667] },
  { id: '128', name: 'Herborn, Germany', coordinates: [50.6889, 8.3042] },
  { id: '129', name: 'Alter Flecken, Germany', coordinates: [50.8018, 7.5615] },
  { id: '130', name: 'Hannover, Germany', coordinates: [52.3759, 9.7320] },
  { id: '131', name: 'Cuxhaven, Germany', coordinates: [53.8567, 8.6942] },
  { id: '132', name: 'Warnemünde, Germany', coordinates: [54.1707, 12.0847] },
  { id: '133', name: 'Passau, Germany', coordinates: [48.5735, 13.4569] },
  { id: '134', name: 'Neuschwanstein Castle, Germany', coordinates: [47.5576, 10.7498] },
  { id: '135', name: 'Rothenburg ob der Tauber, Germany', coordinates: [49.3797, 10.1860] },

  // Chilean Cities
  { id: '136', name: 'Santiago, Chile', coordinates: [-33.4489, -70.6693] },
  { id: '137', name: 'Valparaíso, Chile', coordinates: [-33.0472, -71.6127] },
  { id: '138', name: 'Viña del Mar, Chile', coordinates: [-33.0246, -71.5518] },
  { id: '139', name: 'La Serena, Chile', coordinates: [-29.9027, -71.2520] },
  { id: '140', name: 'Zapallar, Chile', coordinates: [-32.5533, -71.4647] },
  { id: '141', name: 'Cachagua, Chile', coordinates: [-32.5800, -71.4500] },
  { id: '142', name: 'Maitencillo, Chile', coordinates: [-32.6425, -71.4339] },
  { id: '143', name: 'Puerto Varas, Chile', coordinates: [-41.3195, -72.9854] },
  { id: '144', name: 'Concepción, Chile', coordinates: [-36.8270, -73.0498] },
  { id: '145', name: 'Puerto Montt, Chile', coordinates: [-41.4693, -72.9424] },
  { id: '146', name: 'Rapa Nui (Easter Island), Chile', coordinates: [-27.1127, -109.3497] },
  { id: '147', name: 'Pucón, Chile', coordinates: [-39.2822, -71.9540] },
  { id: '148', name: 'Villarrica, Chile', coordinates: [-39.2857, -72.2279] },
  { id: '149', name: 'Valdivia, Chile', coordinates: [-39.8142, -73.2459] },
  { id: '150', name: 'Antofagasta, Chile', coordinates: [-23.6509, -70.3975] },
  { id: '151', name: 'Iquique, Chile', coordinates: [-20.2204, -70.1391] },
  { id: '152', name: 'San Pedro de Atacama, Chile', coordinates: [-22.9087, -68.1997] },

  // Additional Swiss Locations
  { id: '153', name: 'Mount Pilatus, Switzerland', coordinates: [46.9793, 8.2548] },
  { id: '154', name: 'Weggis, Switzerland', coordinates: [47.0297, 8.4351] },

  // Additional Chilean Cities
  { id: '155', name: 'Lago Ranco, Chile', coordinates: [-40.2307, -72.4917] },
  { id: '156', name: 'Osorno, Chile', coordinates: [-40.5739, -73.1335] },
  { id: '157', name: 'Temuco, Chile', coordinates: [-38.7359, -72.5904] },
  { id: '158', name: 'Chillán, Chile', coordinates: [-36.6066, -72.1027] },
  { id: '159', name: 'San Antonio, Chile', coordinates: [-33.5947, -71.6063] },
  { id: '160', name: 'Rocas de Santo Domingo, Chile', coordinates: [-33.6345, -71.6288] },
  { id: '161', name: 'La Ligua, Chile', coordinates: [-32.4524, -71.2311] },
  { id: '162', name: 'Los Vilos, Chile', coordinates: [-31.9125, -71.5096] },
  { id: '163', name: 'Coquimbo, Chile', coordinates: [-29.9533, -71.3436] },
  { id: '164', name: 'Vicuña, Chile', coordinates: [-30.0319, -70.7081] },
  { id: '165', name: 'Copiapó, Chile', coordinates: [-27.3668, -70.3321] },

  // Argentina
  { id: '166', name: 'Buenos Aires, Argentina', coordinates: [-34.6037, -58.3816] },
  { id: '167', name: 'Mendoza, Argentina', coordinates: [-32.8895, -68.8458] },
  { id: '168', name: 'Bariloche, Argentina', coordinates: [-41.1335, -71.3103] },
  { id: '169', name: 'Villa La Angostura, Argentina', coordinates: [-40.7631, -71.6441] },

  // Colombia
  { id: '170', name: 'Medellín, Colombia', coordinates: [6.2442, -75.5812] },
  { id: '171', name: 'Guatapé, Colombia', coordinates: [6.2342, -75.1622] },
  { id: '172', name: 'Bogotá, Colombia', coordinates: [4.7110, -74.0721] },

  // Brazil
  { id: '173', name: 'Rio de Janeiro, Brazil', coordinates: [-22.9068, -43.1729] },
  { id: '174', name: 'São Paulo, Brazil', coordinates: [-23.5505, -46.6333] },

  // Puerto Rico
  { id: '175', name: 'San Juan, Puerto Rico', coordinates: [18.4655, -66.1057] },

  // Dominican Republic
  { id: '176', name: 'Punta Cana, Dominican Republic', coordinates: [18.5601, -68.3725] },

  // Mexico
  { id: '177', name: 'Cancún, Mexico', coordinates: [21.1619, -86.8515] },
  { id: '178', name: 'Playa del Carmen, Mexico', coordinates: [20.6296, -87.0739] },
  { id: '179', name: 'Cozumel, Mexico', coordinates: [20.4230, -86.9223] },

  // United States
  { id: '180', name: 'Miami, Florida, USA', coordinates: [25.7617, -80.1918] },
  { id: '181', name: 'New York City, USA', coordinates: [40.7128, -74.0060] },
  { id: '182', name: 'Washington, D.C., USA', coordinates: [38.9072, -77.0369] },
  { id: '183', name: 'Los Angeles, USA', coordinates: [34.0522, -118.2437] },
  { id: '184', name: 'Chicago, USA', coordinates: [41.8781, -87.6298] },
  { id: '185', name: 'Orlando (Disney World), USA', coordinates: [28.3772, -81.5707] },
  { id: '186', name: 'San Diego, USA', coordinates: [32.7157, -117.1611] },
  { id: '187', name: 'Santa Monica, USA', coordinates: [34.0195, -118.4912] },
  { id: '188', name: 'San Francisco, USA', coordinates: [37.7749, -122.4194] },
  { id: '189', name: 'Santa Barbara, USA', coordinates: [34.4208, -119.6982] },

  // Morocco
  { id: '190', name: 'Fez, Morocco', coordinates: [34.0181, -5.0078] },
  { id: '191', name: 'Chefchaouen, Morocco', coordinates: [35.1716, -5.2697] },

  // Additional Japanese Cities
  { id: '192', name: 'Hiroshima, Japan', coordinates: [34.3853, 132.4553] },
  { id: '193', name: 'Nagoya, Japan', coordinates: [35.1815, 136.9066] },
  { id: '194', name: 'Osaka, Japan', coordinates: [34.6937, 135.5023] },
  { id: '195', name: 'Himeji, Japan', coordinates: [34.8157, 134.6854] },
  { id: '196', name: 'Kanazawa, Japan', coordinates: [36.5626, 136.6562] },
  { id: '197', name: 'Takayama, Japan', coordinates: [36.1408, 137.2520] },

  // China
  { id: '198', name: 'Beijing, China', coordinates: [39.9042, 116.4074] },
  { id: '199', name: 'Shanghai, China', coordinates: [31.2304, 121.4737] },
  { id: '200', name: 'Hong Kong', coordinates: [22.3193, 114.1694] },
  { id: '201', name: 'Shenzhen, China', coordinates: [22.5431, 114.0579] },

  // Vietnam
  { id: '202', name: 'Hué, Vietnam', coordinates: [16.4637, 107.5909] },
  { id: '203', name: 'Hanoi, Vietnam', coordinates: [21.0285, 105.8542] },
  { id: '204', name: 'Ha Long Bay, Vietnam', coordinates: [20.9101, 107.1839] },
  { id: '205', name: 'Da Nang, Vietnam', coordinates: [16.0545, 108.2022] },
  { id: '206', name: 'Ho Chi Minh City, Vietnam', coordinates: [10.8231, 106.6297] },

  // Cambodia
  { id: '207', name: 'Siem Reap, Cambodia', coordinates: [13.3633, 103.8564] },
  { id: '208', name: 'Battambang, Cambodia', coordinates: [13.1023, 103.1960] },

  // Thailand
  { id: '209', name: 'Bangkok, Thailand', coordinates: [13.7563, 100.5018] },
  { id: '210', name: 'Chiang Rai, Thailand', coordinates: [19.9105, 99.8403] },
  { id: '211', name: 'Chiang Mai, Thailand', coordinates: [18.7883, 98.9853] },
  { id: '212', name: 'Phuket, Thailand', coordinates: [7.8804, 98.3923] },
  { id: '213', name: 'Krabi, Thailand', coordinates: [8.0863, 98.9063] },
  { id: '214', name: 'Phi Phi Islands, Thailand', coordinates: [7.7407, 98.7784] },

  // Singapore
  { id: '215', name: 'Singapore', coordinates: [1.3521, 103.8198] },

  // Malaysia
  { id: '216', name: 'Kuala Lumpur, Malaysia', coordinates: [3.1390, 101.6869] },

  // United States
  { id: '217', name: 'Fort Lauderdale, USA', coordinates: [26.1224, -80.1373] },
  { id: '218', name: 'Boca Raton, USA', coordinates: [26.3683, -80.1289] },
  { id: '219', name: 'Monterey, USA', coordinates: [36.6002, -121.8947] },

  // Additional Chilean Cities
  { id: '220', name: 'Frutillar, Chile', coordinates: [-41.1287, -73.0544] },
  { id: '221', name: 'Niebla, Chile', coordinates: [-39.8667, -73.4000] },
  { id: '222', name: 'Caburgua, Chile', coordinates: [-39.2500, -71.8167] },
  { id: '223', name: 'Pisco Elqui, Chile', coordinates: [-30.1242, -70.4931] },

  // Additional Spanish Cities
  { id: '224', name: 'Calpe, Spain', coordinates: [38.6447, 0.0451] },
  { id: '225', name: 'Cala del Portitxol, Spain', coordinates: [38.7833, 0.1994] },
  { id: '226', name: 'Jávea, Spain', coordinates: [38.7880, 0.1628] },
  { id: '227', name: 'Altea, Spain', coordinates: [38.5990, 0.0494] },
  { id: '228', name: 'Xàtiva, Spain', coordinates: [38.9889, -0.5198] },
  { id: '229', name: 'Peñíscola, Spain', coordinates: [40.3593, 0.4058] },
  { id: '230', name: 'Segovia, Spain', coordinates: [40.9429, -4.1088] },
  { id: '231', name: 'Valle de los Caídos, Spain', coordinates: [40.6419, -4.1550] },

  // Additional Irish Locations
  { id: '232', name: "Giant's Causeway, Northern Ireland", coordinates: [55.2408, -6.5116] },
  { id: '233', name: 'Bundoran, Ireland', coordinates: [54.4790, -8.2797] },
  { id: '234', name: 'Galway, Ireland', coordinates: [53.2707, -9.0568] },
  { id: '235', name: 'Cliffs of Moher, Ireland', coordinates: [52.9715, -9.4309] },

  // Indonesia
  { id: '236', name: 'Nusa Penida, Indonesia', coordinates: [-8.7346, 115.5444] },
  { id: '237', name: 'Ubud, Indonesia', coordinates: [-8.5069, 115.2625] },
  { id: '238', name: 'Yogyakarta, Indonesia', coordinates: [-7.7956, 110.3695] },

  // Philippines
  { id: '239', name: 'Cebu City, Philippines', coordinates: [10.3157, 123.8854] },
  { id: '240', name: 'Chocolate Hills, Bohol, Philippines', coordinates: [9.8296, 124.1387] },
  { id: '241', name: 'El Nido, Philippines', coordinates: [11.1927, 119.4127] },

  // Myanmar
  { id: '242', name: 'Yangon, Myanmar', coordinates: [16.8661, 96.1951] },
  { id: '243', name: 'Mandalay, Myanmar', coordinates: [21.9588, 96.0891] },
  { id: '244', name: 'Bagan, Myanmar', coordinates: [21.1717, 94.8585] },

  // Additional Italian Location
  { id: '245', name: 'Porto Di Savona, Italy', coordinates: [44.3076, 8.4801] },

  // Additional Austrian Location
  { id: '246', name: 'Hallstatt, Austria', coordinates: [47.5622, 13.6493] },

  // Additional German Location
  { id: '247', name: 'Rüdesheim am Rhein, Germany', coordinates: [49.9770, 7.9233] },

  // Additional Indonesian Location
  { id: '248', name: 'Uluwatu, Indonesia', coordinates: [-8.8291, 115.0849] },

  // Vatican City
  { id: '249', name: 'Vatican City', coordinates: [41.9029, 12.4534] },

  // Haiti
  { id: '250', name: 'Labadee, Haiti', coordinates: [19.7769, -72.2475] },

  // Turkey
  { id: '251', name: 'Kusadasi, Turkey', coordinates: [37.8579, 27.2610] },

  // United Arab Emirates
  { id: '252', name: 'Abu Dhabi, UAE', coordinates: [24.4539, 54.3773] },

  // Additional Japanese Location
  { id: '253', name: 'Nara, Japan', coordinates: [34.6851, 135.8048] },

  // Bulgaria
  { id: '254', name: 'Sofia, Bulgaria', coordinates: [42.6977, 23.3219] },
  { id: '255', name: 'Rila Monastery, Bulgaria', coordinates: [42.1335, 23.3402] },

  // Additional Serbian Location
  { id: '256', name: 'Belgrade, Serbia', coordinates: [44.7866, 20.4489] },

  // Additional Slovak Location
  { id: '257', name: 'Bratislava, Slovakia', coordinates: [48.1486, 17.1077] },

  // Additional Polish Location
  { id: '258', name: 'Auschwitz-Birkenau, Poland', coordinates: [50.0270, 19.2021] },

  // Additional Greek Location
  { id: '259', name: 'Meteora, Greece', coordinates: [39.7217, 21.6306] },

  // Additional Italian Locations
  { id: '260', name: 'Sorrento, Italy', coordinates: [40.6263, 14.3757] },
  { id: '261', name: 'Capri, Italy', coordinates: [40.5532, 14.2222] },
  { id: '262', name: 'Amalfi, Italy', coordinates: [40.6341, 14.6026] },
  { id: '263', name: 'Positano, Italy', coordinates: [40.6280, 14.4849] },
  { id: '264', name: 'Pompei, Italy', coordinates: [40.7462, 14.4989] },
  { id: '265', name: 'Lucca, Italy', coordinates: [43.8429, 10.5027] },
  { id: '266', name: 'Cinque Terre, Italy', coordinates: [44.1279, 9.7100] },
  { id: '267', name: 'Portofino, Italy', coordinates: [44.3036, 9.2097] },
  { id: '268', name: 'Rapallo, Italy', coordinates: [44.3488, 9.2311] },
  { id: '269', name: 'Genova, Italy', coordinates: [44.4056, 8.9463] },
  { id: '270', name: 'Bergamo, Italy', coordinates: [45.6983, 9.6773] },
  { id: '271', name: 'Porto Cervo, Italy', coordinates: [41.1359, 9.5370] },

  // Liechtenstein
  { id: '272', name: 'Vaduz, Liechtenstein', coordinates: [47.1410, 9.5209] },

  // Additional German Location
  { id: '273', name: 'Münster, Germany', coordinates: [51.9607, 7.6261] },

  // Additional Croatian Locations
  { id: '274', name: 'Hvar, Croatia', coordinates: [43.1729, 16.4411] },
  { id: '275', name: 'Split, Croatia', coordinates: [43.5081, 16.4402] },

  // Additional German Locations
  { id: '276', name: 'Potsdam, Germany', coordinates: [52.3906, 13.0645] },
  { id: '277', name: 'Luxembourg, Luxembourg', coordinates: [49.6116, 6.1319] },
  { id: '278', name: 'Krefeld, Germany', coordinates: [51.3388, 6.5853] },
  { id: '279', name: 'Neuss, Germany', coordinates: [51.2042, 6.6879] },
  { id: '280', name: 'Dortmund, Germany', coordinates: [51.5136, 7.4653] },
  { id: '281', name: 'Breskens, Netherlands', coordinates: [51.3958, 3.5556] },
  { id: '282', name: 'Chisinau, Moldova', coordinates: [47.0105, 28.8638] },
];

function MediaCarousel({ media }: { media: Location['media'] }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const currentMedia = media[currentIndex];

  return (
    <div className="media-carousel">
      {currentMedia.type === 'image' ? (
        <img 
          src={currentMedia.url} 
          alt={currentMedia.caption || 'Location image'} 
          className="media-item"
        />
      ) : (
        <video 
          src={currentMedia.url} 
          controls 
          className="media-item"
        />
      )}
      {currentMedia.caption && (
        <div className="carousel-caption">{currentMedia.caption}</div>
      )}
      {media.length > 1 && (
        <div className="carousel-controls">
          {media.map((_, index) => (
            <div
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TravelMap() {
  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <style>{styleSheet}</style>
      <MapContainer
        center={[48.8566, 15.9819]}
        zoom={4}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        zoomControl={false}
        minZoom={3}
      >
        <ZoomControl position="bottomright" />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        {travelLocations.map((location) => (
          <Marker
            key={location.id}
            position={location.coordinates}
            icon={customIcon}
          >
            <Popup>
              <div className="popup-content">
                <h3>{location.name}</h3>
                {location.description && (
                  <p>{location.description}</p>
                )}
                {location.date && (
                  <p className="text-xs mt-1">{location.date}</p>
                )}
                {location.media && <MediaCarousel media={location.media} />}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
} 